import React, { useEffect, useMemo, useState } from "react";
import mailboxAxios from "../../utils/mailboxAxios";
import { useNavigate } from "react-router-dom";

function StatCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-4 shadow-sm">
      <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
      <div className="mt-1 text-2xl font-bold text-black dark:text-white">{value}</div>
    </div>
  );
}

function Badge({ children, tone = "default" }) {
  const tones = {
    green:
      "bg-green-50 text-green-700 border-green-200 dark:bg-green-950/30 dark:text-green-300 dark:border-green-900",
    yellow:
      "bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-950/30 dark:text-yellow-300 dark:border-yellow-900",
    red:
      "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/30 dark:text-red-300 dark:border-red-900",
    blue:
      "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-900",
    gray:
      "bg-gray-50 text-gray-700 border-gray-200 dark:bg-neutral-800 dark:text-gray-300 dark:border-neutral-700",
    default:
      "bg-gray-50 text-gray-700 border-gray-200 dark:bg-neutral-800 dark:text-gray-300 dark:border-neutral-700",
  };

  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${tones[tone] || tones.default}`}
    >
      {children}
    </span>
  );
}

function PositionTags({ positions }) {
  if (!positions) {
    return <span className="text-gray-400 dark:text-gray-500">-</span>;
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {String(positions)
        .split(",")
        .map((p) => p.trim())
        .filter(Boolean)
        .map((pos) => (
          <span
            key={pos}
            className="inline-flex items-center px-2.5 py-1 rounded-full text-xs border bg-gray-50 text-gray-700 border-gray-200 dark:bg-neutral-800 dark:text-gray-300 dark:border-neutral-700"
          >
            {pos}
          </span>
        ))}
    </div>
  );
}

function computeHealth(row) {
  if (!row.linked) {
    return {
      tone: "red",
      label: "No HR link",
      rank: 0,
    };
  }

  if (!row.hasPositions) {
    return {
      tone: "yellow",
      label: "No active position",
      rank: 1,
    };
  }

  if (Number(row.roles_count || 0) === 0) {
    return {
      tone: "yellow",
      label: "No roles",
      rank: 2,
    };
  }

  if (Number(row.policies_count || 0) === 0) {
    return {
      tone: "yellow",
      label: "No policies",
      rank: 3,
    };
  }

  if (Number(row.permissions_count || 0) === 0) {
    return {
      tone: "yellow",
      label: "No permissions",
      rank: 4,
    };
  }

  return {
    tone: "green",
    label: "OK",
    rank: 5,
  };
}

function summaryText(row) {
  const roles = Number(row.roles_count || 0);
  const policies = Number(row.policies_count || 0);
  const permissions = Number(row.permissions_count || 0);
  const modules = Number(row.visible_modules_count || 0);

  const hasAnyDerived =
    roles > 0 || policies > 0 || permissions > 0 || modules > 0;

  if (!hasAnyDerived) {
    return "No derived access";
  }

  return `${roles} roles · ${policies} policies · ${permissions} perms · ${modules} modules`;
}

export default function ITAdminAccessPage() {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [q, setQ] = useState("");
  const [linkFilter, setLinkFilter] = useState("all");
  const [positionFilter, setPositionFilter] = useState("all");
  const [healthFilter, setHealthFilter] = useState("all");

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await mailboxAxios.get("/admin/access/users");
      setRows(res?.data?.data || []);
    } catch (err) {
      console.error("ITAdminAccessPage load error:", err);
      setError(
        err?.response?.data?.message ||
          err.message ||
          "Failed to load access users."
      );
    } finally {
      setLoading(false);
    }
  };

  const normalizedRows = useMemo(() => {
    return rows.map((r) => {
      const linked = Boolean(r.person_id);
      const hasPositions = Boolean(r.positions && String(r.positions).trim());
      const health = computeHealth({
        ...r,
        linked,
        hasPositions,
      });

      return {
        ...r,
        linked,
        hasPositions,
        health,
      };
    });
  }, [rows]);

  const filteredRows = useMemo(() => {
    const needle = q.trim().toLowerCase();

    return normalizedRows.filter((r) => {
      if (needle) {
        const haystack = [
          r.display_name,
          r.email,
          r.full_name,
          r.positions,
          r.health?.label,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        if (!haystack.includes(needle)) return false;
      }

      if (linkFilter === "linked" && !r.linked) return false;
      if (linkFilter === "unlinked" && r.linked) return false;

      if (positionFilter === "with_position" && !r.hasPositions) return false;
      if (positionFilter === "without_position" && r.hasPositions) return false;

      if (healthFilter === "ok" && r.health?.label !== "OK") return false;
      if (healthFilter === "warning" && r.health?.label === "OK") return false;
      if (healthFilter === "no_hr_link" && r.health?.label !== "No HR link") return false;
      if (
        healthFilter === "no_active_position" &&
        r.health?.label !== "No active position"
      )
        return false;

      return true;
    });
  }, [normalizedRows, q, linkFilter, positionFilter, healthFilter]);

  const stats = useMemo(() => {
    const total = normalizedRows.length;
    const linked = normalizedRows.filter((r) => r.linked).length;
    const unlinked = total - linked;
    const withPositions = normalizedRows.filter((r) => r.hasPositions).length;
    const healthy = normalizedRows.filter((r) => r.health?.label === "OK").length;

    return { total, linked, unlinked, withPositions, healthy };
  }, [normalizedRows]);

  if (loading) {
    return <div className="p-6 text-black dark:text-white">Loading IT access panel...</div>;
  }

  if (error) {
    return <div className="p-6 text-red-600 dark:text-red-400">{error}</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-black dark:text-white">
            IT Access Panel
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Central overview of users, HR link status and effective access readiness.
          </p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={load}
            className="px-4 py-2 rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-black dark:text-white hover:bg-gray-50 dark:hover:bg-neutral-800 transition"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        <StatCard label="Total users" value={stats.total} />
        <StatCard label="Linked to HR person" value={stats.linked} />
        <StatCard label="Unlinked users" value={stats.unlinked} />
        <StatCard label="Users with positions" value={stats.withPositions} />
        <StatCard label="Access health OK" value={stats.healthy} />
      </div>

      <div className="rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-4 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by user, email, person, position or status..."
            className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white outline-none"
          />

          <select
            value={linkFilter}
            onChange={(e) => setLinkFilter(e.target.value)}
            className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white outline-none"
          >
            <option value="all">All link states</option>
            <option value="linked">Linked only</option>
            <option value="unlinked">Unlinked only</option>
          </select>

          <select
            value={positionFilter}
            onChange={(e) => setPositionFilter(e.target.value)}
            className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white outline-none"
          >
            <option value="all">All position states</option>
            <option value="with_position">With position</option>
            <option value="without_position">Without position</option>
          </select>

          <select
            value={healthFilter}
            onChange={(e) => setHealthFilter(e.target.value)}
            className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white outline-none"
          >
            <option value="all">All access health</option>
            <option value="ok">OK</option>
            <option value="warning">Warnings only</option>
            <option value="no_hr_link">No HR link</option>
            <option value="no_active_position">No active position</option>
          </select>

          <button
            onClick={() => {
              setQ("");
              setLinkFilter("all");
              setPositionFilter("all");
              setHealthFilter("all");
            }}
            className="rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white hover:bg-gray-50 dark:hover:bg-neutral-800 transition"
          >
            Reset filters
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1280px]">
            <thead className="bg-gray-50 dark:bg-neutral-800">
              <tr className="border-b border-gray-200 dark:border-neutral-700">
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                  User
                </th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                  HR Link
                </th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                  Person
                </th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                  Positions
                </th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                  Access Health
                </th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                  Access Summary
                </th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {filteredRows.map((r) => (
                <tr
                  key={r.user_id}
                  className="border-b border-gray-100 dark:border-neutral-800 hover:bg-gray-50 dark:hover:bg-neutral-800/60 transition"
                >
                  <td className="p-3 align-top">
                    <div className="font-medium text-black dark:text-white">
                      {r.display_name || "-"}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {r.email || "-"}
                    </div>
                  </td>

                  <td className="p-3 align-top">
                    {r.linked ? (
                      <Badge tone="green">Linked</Badge>
                    ) : (
                      <Badge tone="red">Unlinked</Badge>
                    )}
                  </td>

                  <td className="p-3 align-top text-black dark:text-white">
                    {r.full_name || (
                      <span className="text-gray-400 dark:text-gray-500">-</span>
                    )}
                  </td>

                  <td className="p-3 align-top">
                    <PositionTags positions={r.positions} />
                  </td>

                  <td className="p-3 align-top">
                    <Badge tone={r.health?.tone}>{r.health?.label}</Badge>
                  </td>

                  <td className="p-3 align-top">
                    <div className="text-black dark:text-white text-sm">
                      {summaryText(r)}
                    </div>
                  </td>

                  <td className="p-3 align-top">
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => navigate(`/it/access/users/${r.user_id}`)}
                        className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 text-black dark:text-white hover:bg-gray-50 dark:hover:bg-neutral-800 transition"
                      >
                        Explain Access
                      </button>

                      {r.person_id ? (
                        <button
                          onClick={() => navigate(`/hr/people/${r.person_id}`)}
                          className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 text-black dark:text-white hover:bg-gray-50 dark:hover:bg-neutral-800 transition"
                        >
                          Open HR
                        </button>
                      ) : (
                        <button
                          onClick={() =>
                            navigate(`/hr/people?q=${encodeURIComponent(r.email || r.display_name || "")}`)
                          }
                          className="px-3 py-1.5 rounded-lg border border-yellow-300 text-yellow-700 bg-yellow-50 dark:bg-yellow-950/30 dark:text-yellow-300 dark:border-yellow-900"
                          type="button"
                        >
                          Link HR person
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}

              {filteredRows.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="p-8 text-center text-gray-500 dark:text-gray-400"
                  >
                    No users match the selected filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}